import type { FAQItem } from '../../seo/FAQSchema';
import type { RelatedLink } from '../Common/RelatedLinks';

export type LogisticsClusterId = 'ecommerce' | 'b2b' | 'warehousing';

export type LogisticsClusterTile = {
  id: string;
  title: string;
  copy: string;
};

export type LogisticsClusterStep = {
  id: string;
  title: string;
  body: string;
};

export type LogisticsClusterConfig = {
  id: LogisticsClusterId;
  path: string;
  ariaLabel: string;
  titleLight: string;
  titleAccent: string;
  heroFeatures: string[];
  ctaLabel: string;
  whatsappPrefill: string;
  tiles: LogisticsClusterTile[];
  audienceTitle: string;
  audienceLead: string;
  audiencePoints: string[];
  howTitle: string;
  howLead: string;
  steps: LogisticsClusterStep[];
  faqs: FAQItem[];
  relatedServices: RelatedLink[];
  relatedBlog: RelatedLink;
};

/** Hub cards on /logistics-services — one keyword cluster each. */
export const logisticsHubClusters: Array<{
  id: LogisticsClusterId;
  to: string;
  title: string;
  copy: string;
  keywordsHint: string;
}> = [
  {
    id: 'ecommerce',
    to: '/ecommerce-fulfillment-services',
    title: 'Ecommerce fulfilment services UK',
    copy:
      'Pick, pack and dispatch for Shopify, Amazon, TikTok and multi-channel brands — built for DTC order volume, carrier SLAs and Mintsoft inventory sync.',
    keywordsHint: 'Ecommerce fulfilment · fulfilment companies UK',
  },
  {
    id: 'b2b',
    to: '/b2b-fulfillment',
    title: 'B2B fulfilment UK',
    copy:
      'Wholesale and trade deliveries with timed slots, ASN/SSCC detail and palletised dispatch to retailers, buying groups and distribution centres.',
    keywordsHint: 'B2B fulfilment · B2B ecommerce fulfilment',
  },
  {
    id: 'warehousing',
    to: '/warehousing-services',
    title: 'Warehousing services UK',
    copy:
      'Ecommerce warehousing in Milton Keynes — barcode receiving, secure storage, stock visibility and outbound into pick & pack or FBA inbound.',
    keywordsHint: 'Ecommerce warehousing · warehousing and fulfilment',
  },
];

export const logisticsClusterConfigs: Record<LogisticsClusterId, LogisticsClusterConfig> = {
  ecommerce: {
    id: 'ecommerce',
    path: '/ecommerce-fulfillment-services',
    ariaLabel: 'Ecommerce fulfilment services UK',
    titleLight: '3pl warehouse for commercehub UK & Ecommerce Fulfilment:',
    titleAccent: 'Pick, Pack & Dispatch for Online Brands',
    heroFeatures: [
      'Same-day cut-offs available',
      'Shopify, Amazon & TikTok ready',
      '99.9% pick accuracy target',
      'Transparent 3PL pricing',
    ],
    ctaLabel: 'Get an ecommerce fulfilment quote',
    whatsappPrefill:
      'Hi Pick Pack Pro — I would like to discuss ecommerce fulfilment services for my online store.',
    tiles: [
      {
        id: 'pick-pack',
        title: 'Pick and pack fulfilment',
        copy:
          'Barcode-led picks, branded packing options and carrier booking for DTC orders — so ecommerce fulfilment stays accurate when volumes spike.',
      },
      {
        id: 'channels',
        title: 'Multi-channel ecommerce brands',
        copy:
          'One warehouse stack for Shopify, marketplaces and TikTok Shop — inventory sync and order routing without juggling separate 3PLs.',
      },
      {
        id: 'dispatch',
        title: 'UK carrier dispatch',
        copy:
          'Next-day and economy options from Milton Keynes with tracked labels and SLA-minded cut-offs for growing ecommerce fulfilment companies’ customers.',
      },
      {
        id: 'returns',
        title: 'Returns into stock',
        copy:
          'Customer returns checked, graded and put away so sellable stock is back on the shelf quickly — linked to the same WMS as outbound.',
      },
    ],
    audienceTitle: 'Who ecommerce fulfilment is for',
    audienceLead:
      'Built for UK online sellers who need a fulfilment partner — not a generic courier account.',
    audiencePoints: [
      'Shopify and DTC brands outgrowing garage or in-house packing',
      'Marketplace sellers consolidating inventory into one UK 3PL',
      'TikTok and viral brands that need spike-ready pick capacity',
      'Teams comparing ecommerce fulfilment companies on accuracy and cut-offs',
    ],
    howTitle: 'How ecommerce fulfilment works with Pick Pack Pro',
    howLead: 'From onboarding SKUs to first dispatch — typically days, not weeks.',
    steps: [
      {
        id: '1',
        title: 'Onboard & map SKUs',
        body: 'We import catalogue data, packaging rules and channel connections into Mintsoft so stock and orders stay aligned.',
      },
      {
        id: '2',
        title: 'Receive & put away',
        body: 'Inbound cartons and pallets are scanned into locations — ready for pick waves the same day goods clear QC.',
      },
      {
        id: '3',
        title: 'Pick, pack & ship',
        body: 'Orders release to the floor against your cut-offs; carriers are booked and tracking flows back to your storefronts.',
      },
      {
        id: '4',
        title: 'Report & scale',
        body: 'Live inventory, exception notes and a dedicated account manager — so you can grow order volume without rebuilding ops.',
      },
    ],
    faqs: [
      {
        question: 'What is ecommerce fulfilment?',
        answer:
          'Ecommerce fulfilment is the complete process of receiving, storing, picking, packing, and shipping online orders to customers. Pick Pack Pro is a UK-based 3PL and Amazon FBA prep centre operating from Milton Keynes, supporting Amazon, Shopify, TikTok Shop and multi-channel sellers with reliable ecommerce fulfillment services.',
      },
      {
        question: 'What do ecommerce fulfillment services include?',
        answer:
          'Our ecommerce fulfillment services cover inbound receiving, secure WMS storage, precise picking, branded packing, carrier booking, and returns processing. Pick Pack Pro handles this entire supply chain with 99.9% accuracy from our central UK hub.',
      },
      {
        question: 'Do you support Shopify and marketplaces from one warehouse?',
        answer:
          'Yes. Pick Pack Pro provides seamless multi-channel ecommerce fulfilment so your Shopify, Amazon, and TikTok Shop channels can share one central inventory pool without splitting stock across multiple 3PLs.',
      },
      {
        question: 'Why choose Pick Pack Pro over other fulfilment companies UK?',
        answer:
          'As a 5.0-star rated 3PL and Amazon FBA prep centre, Pick Pack Pro offers transparent pricing, 99.9% picking accuracy, and a dedicated ecommerce fulfillment solution that scales seamlessly with your business growth.',
      },
    ],
    relatedServices: [
      { to: '/logistics-services', label: 'Logistics services hub' },
      { to: '/multi-channel-fulfillment-services', label: 'Multi-channel fulfilment' },
      { to: '/shopify-fulfillment-services', label: 'Shopify fulfilment UK' },
      { to: '/b2b-fulfillment', label: 'B2B fulfilment' },
      { to: '/warehousing-services', label: 'Warehousing services' },
    ],
    relatedBlog: {
      to: '/blog/ecommerce-fulfilment-guide-uk',
      label: 'Ecommerce fulfilment guide UK',
    },
  },
  b2b: {
    id: 'b2b',
    path: '/b2b-fulfillment',
    ariaLabel: 'B2B fulfilment UK',
    titleLight: 'B2B Fulfilment UK:',
    titleAccent: 'Wholesale, Trade & Retailer Deliveries',
    heroFeatures: [
      'Timed delivery slots',
      'ASN / SSCC ready',
      'Pallet & carton wholesale',
      'Trade DC experience',
    ],
    ctaLabel: 'Get a B2B fulfilment quote',
    whatsappPrefill:
      'Hi Pick Pack Pro — I would like to discuss B2B fulfilment and wholesale delivery for my brand.',
    tiles: [
      {
        id: 'wholesale',
        title: 'Wholesale order fulfilment',
        copy:
          'Multi-unit and case-pick wholesale orders packed to retailer specs — so B2B ecommerce fulfilment matches what trade buyers expect on the dock.',
      },
      {
        id: 'slots',
        title: 'Booked delivery windows',
        copy:
          'We arrange timed slots with retail DCs and wholesale partners, then chase PODs when the gate needs proof.',
      },
      {
        id: 'labels',
        title: 'Trade labelling & ASN detail',
        copy:
          'SSCC, carton labels and ASN fields your buyer asks for — not DTC packing slips stuck on a pallet.',
      },
      {
        id: 'hybrid',
        title: 'B2B + DTC in one hub',
        copy:
          'Run wholesale and consumer channels from the same Milton Keynes stock without splitting inventory across two 3PLs.',
      },
    ],
    audienceTitle: 'Who B2B fulfilment is for',
    audienceLead:
      'For brands selling to retailers, buying groups and trade accounts — not only parcel-to-door customers.',
    audiencePoints: [
      'Wholesale Amazon / marketplace programmes with carton requirements',
      'Brands supplying indie retail and regional buying groups',
      'Teams that need B2B fulfilment with booked unloading windows',
      'Sellers combining DTC ecommerce with trade pallet drops',
    ],
    howTitle: 'How B2B fulfilment works',
    howLead: 'Trade-ready process from PO to proof of delivery.',
    steps: [
      {
        id: '1',
        title: 'Confirm buyer requirements',
        body: 'We capture ASN, labelling, pallet height and booking rules before the first wholesale wave.',
      },
      {
        id: '2',
        title: 'Pick & build consignments',
        body: 'Case and unit picks are assembled, labelled and photographed where the DC requires exception evidence.',
      },
      {
        id: '3',
        title: 'Book & dispatch',
        body: 'Carrier or pallet network booking matches the slot; tracking and POD feed back to your account manager.',
      },
      {
        id: '4',
        title: 'Reconcile exceptions',
        body: 'Refusals and short-ships are logged with photos so finance and sales can resolve claims quickly.',
      },
    ],
    faqs: [
      {
        question: 'What is B2B fulfilment?',
        answer:
          'B2B fulfilment is warehouse fulfilment for business buyers — wholesale cartons, palletised trade deliveries and retailer DC drops — rather than single-parcel DTC. Pick Pack Pro handles B2B fulfilment UK alongside ecommerce channels when needed.',
      },
      {
        question: 'Can you do B2B ecommerce fulfilment and DTC from one site?',
        answer:
          'Yes. Stock can sit in one Milton Keynes warehouse with separate packing rules for wholesale vs consumer orders, so you avoid splitting inventory.',
      },
      {
        question: 'Do you support timed delivery slots?',
        answer:
          'Yes. We book windows with retail and wholesale DCs where required and provide POD documentation for your buyer’s goods-in process.',
      },
      {
        question: 'Is pallet distribution the same as B2B fulfilment?',
        answer:
          'Pallet haulage is the transport leg. B2B fulfilment also covers picking, labelling and preparing the consignment. See our logistics hub for inbound freight and pallet network moves when you only need transport.',
      },
    ],
    relatedServices: [
      { to: '/logistics-services', label: 'Logistics services hub' },
      { to: '/ecommerce-fulfillment-services', label: 'Ecommerce fulfilment services' },
      { to: '/warehousing-services', label: 'Warehousing services' },
      { to: '/freight-forwarder-uk', label: 'Freight forwarder UK' },
      { to: '/amazon-fba-shipments', label: 'Amazon FBA shipments' },
    ],
    relatedBlog: {
      to: '/blog/3pl-logistics-uk-explained-2026',
      label: '3PL logistics UK explained (2026)',
    },
  },
  warehousing: {
    id: 'warehousing',
    path: '/warehousing-services',
    ariaLabel: 'Warehousing services UK',
    titleLight: 'Company Warehouses in UK: Warehousing Services -',
    titleAccent: 'Ecommerce Storage with Fulfilment Ready Stock',
    heroFeatures: [
      'Barcode receiving',
      'Secure MK warehouse',
      'Live stock visibility',
      'Fulfilment-ready putaway',
    ],
    ctaLabel: 'Get a warehousing quote',
    whatsappPrefill:
      'Hi Pick Pack Pro — I would like to discuss ecommerce warehousing and storage in Milton Keynes.',
    tiles: [
      {
        id: 'storage',
        title: 'Ecommerce warehousing',
        copy:
          'Ambient storage for cartons and pallets with location control — built for online sellers who need warehousing and fulfilment under one roof.',
      },
      {
        id: 'receiving',
        title: 'Inbound receiving & QC',
        copy:
          'Supplier deliveries scanned against ASN, exceptions photographed and stock available for pick or FBA prep without spreadsheet chaos.',
      },
      {
        id: 'visibility',
        title: 'WMS stock visibility',
        copy:
          'Mintsoft shows on-hand, allocated and inbound so your ops team trusts the numbers before launching campaigns.',
      },
      {
        id: 'outbound',
        title: 'Outbound into fulfilment',
        copy:
          'The same warehouse feeds pick & pack, B2B wholesale and Amazon FBA inbound — so warehousing is not a dead-end storage shed.',
      },
    ],
    audienceTitle: 'Who warehousing services are for',
    audienceLead:
      'For brands that need UK storage tied to real fulfilment — not passive self-storage.',
    audiencePoints: [
      'Importers staging stock before ecommerce or FBA release',
      'Sellers consolidating 3PLs into one Milton Keynes hub',
      'Seasonal brands needing flexible pallet and carton space',
      'Teams comparing warehousing and fulfilment as a single partner',
    ],
    howTitle: 'How warehousing works at Pick Pack Pro',
    howLead: 'Receive once — fulfil many ways from the same locations.',
    steps: [
      {
        id: '1',
        title: 'Book inbound',
        body: 'Share ASN or packing list; we schedule receiving at our Milton Keynes fulfilment centre.',
      },
      {
        id: '2',
        title: 'Scan & put away',
        body: 'Goods are checked, barcode-logged and placed into pick or bulk locations in the WMS.',
      },
      {
        id: '3',
        title: 'Store with visibility',
        body: 'You see stock levels and movements online — ready for promotions, wholesale POs or FBA shipments.',
      },
      {
        id: '4',
        title: 'Release outbound',
        body: 'Trigger ecommerce fulfilment, B2B fulfilment or FBA prep from the same stored inventory.',
      },
    ],
    faqs: [
      {
        question: 'What warehousing services do you offer?',
        answer:
          'Ecommerce warehousing in Milton Keynes: receiving, barcode putaway, secure ambient storage, inventory reporting and release into pick & pack, B2B or Amazon FBA workflows.',
      },
      {
        question: 'Is this just storage or warehousing and fulfilment?',
        answer:
          'Both. You can store only if needed, but most brands use warehousing and fulfilment together so stock is fulfilment-ready the moment orders land.',
      },
      {
        question: 'Where is your warehouse?',
        answer:
          'Pick Pack Pro operates from Milton Keynes (central UK motorway access) — ideal for nationwide ecommerce dispatch and inbound from UK/EU suppliers.',
      },
      {
        question: 'How does warehousing relate to logistics services?',
        answer:
          'Warehousing holds and controls stock. Logistics covers inbound freight and distribution legs. Start on this page for storage; use the logistics hub when you need transport into or out of the warehouse.',
      },
    ],
    relatedServices: [
      { to: '/logistics-services', label: 'Logistics services hub' },
      { to: '/our-fulfilment-center', label: 'Our fulfilment centre' },
      { to: '/ecommerce-fulfillment-services', label: 'Ecommerce fulfilment services' },
      { to: '/b2b-fulfillment', label: 'B2B fulfilment' },
      { to: '/wms-integrations', label: 'WMS integrations' },
    ],
    relatedBlog: {
      to: '/blog/how-to-outsource-ecommerce-fulfilment',
      label: 'How to outsource ecommerce fulfilment',
    },
  },
};
